"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_MARGIN = 8;

/**
 * Standard Bastion tooltip — appears above the trigger element.
 *
 * Usage:
 *   <BastionTooltip text="Market Cap">
 *     <button>MCAP</button>
 *   </BastionTooltip>
 *
 *   <BastionTooltip content={<><b>Score 95</b><br/>Top 5%</>}>
 *     <span>95</span>
 *   </BastionTooltip>
 *
 * Props:
 *   text      — simple string tooltip (mutually exclusive with content)
 *   content   — rich ReactNode tooltip (takes precedence over text)
 *   maxWidth  — tooltip max width (default 220)
 *   offset    — gap between trigger bottom edge and tooltip (default 6)
 *   disabled  — skip rendering the tooltip entirely
 *   zIndex    — stacking above portaled pickers (default 9999; use 10001+ inside list popovers)
 *   variant   — "default" (standard) or "metric" (Today's Picks / screener column header style)
 *   fullWidth — trigger wrapper spans the parent width (row-shaped triggers)
 */
export default function BastionTooltip({
  text,
  content,
  children,
  maxWidth = 220,
  offset,
  disabled = false,
  zIndex = 9999,
  variant = "default",
  fullWidth = false,
}: {
  text?: string;
  content?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: number;
  offset?: number;
  disabled?: boolean;
  zIndex?: number;
  variant?: "default" | "metric";
  fullWidth?: boolean;
}) {
  const isMetric = variant === "metric";
  const tooltipOffset = offset ?? (isMetric ? 16 : 6);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0, translateX: "-50%" });
  const ref = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) setShow(false);
  }, [disabled]);
  useEffect(() => {
    if (!show) return;
    const hide = () => setShow(false);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    window.addEventListener("touchstart", hide, { passive: true });
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      window.removeEventListener("touchstart", hide);
    };
  }, [show]);

  const onEnter = useCallback(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({
        x: r.left + r.width / 2,
        y: r.top - tooltipOffset,
        translateX: "-50%",
      });
    }
    setShow(true);
  }, [tooltipOffset]);

  const body = content ?? text;

  const fitMaxWidth =
    typeof window !== "undefined"
      ? Math.min(maxWidth, window.innerWidth - VIEWPORT_MARGIN * 2)
      : maxWidth;

  useLayoutEffect(() => {
    if (!show || !tooltipRef.current || !ref.current) return;

    const tip = tooltipRef.current;
    const trigger = ref.current.getBoundingClientRect();
    const w = tip.offsetWidth;
    const half = w / 2;
    const viewportW = window.innerWidth;
    const maxCenter = viewportW - VIEWPORT_MARGIN - half;
    const minCenter = VIEWPORT_MARGIN + half;

    setPos((prev) => {
      let x = prev.x;
      let translateX = "-50%";

      if (minCenter > maxCenter) {
        x = VIEWPORT_MARGIN;
        translateX = "0%";
      } else if (x > maxCenter) {
        x = Math.min(trigger.right, viewportW - VIEWPORT_MARGIN);
        translateX = "-100%";
        if (x - w < VIEWPORT_MARGIN) {
          x = VIEWPORT_MARGIN;
          translateX = "0%";
        }
      } else if (x < minCenter) {
        x = Math.max(trigger.left, VIEWPORT_MARGIN);
        translateX = "0%";
        if (x + w > viewportW - VIEWPORT_MARGIN) {
          x = viewportW - VIEWPORT_MARGIN;
          translateX = "-100%";
        }
      }

      if (x === prev.x && translateX === prev.translateX) return prev;
      return { ...prev, x, translateX };
    });
  }, [show, text, content, maxWidth]);

  if (!body || disabled) return <>{children}</>;

  // Short labels ("Share screenshot") stay one line → pill shape like other Bastion chips.
  const isShortLabel =
    typeof body === "string" && body.length > 0 && body.length <= 48 && !body.includes("\n");
  const tipRadius = isMetric || isShortLabel ? 999 : 10;

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={onEnter}
        onMouseLeave={() => setShow(false)}
        style={{
          cursor: "inherit",
          display: fullWidth ? "flex" : "inline-flex",
          width: fullWidth ? "100%" : undefined,
          alignItems: "center",
          lineHeight: "normal",
          verticalAlign: "middle",
        }}
      >
        {children}
      </span>
      {show &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              left: pos.x,
              top: pos.y,
              transform: `translate(${pos.translateX}, -100%)`,
              fontSize: 11,
              fontWeight: 500,
              lineHeight: isMetric ? 1.3 : 1.4,
              color: isMetric ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.75)",
              background: isMetric ? "#2a2a2a" : "#1a1a1a",
              border: isMetric
                ? "1px solid rgba(255,255,255,0.10)"
                : "1px solid rgba(255,255,255,0.12)",
              borderRadius: tipRadius,
              padding: isMetric ? "6px 10px 8px" : isShortLabel ? "5px 11px" : "6px 10px",
              pointerEvents: "none",
              zIndex,
              maxWidth: isMetric ? undefined : fitMaxWidth,
              textAlign: isMetric ? "left" : "center",
              whiteSpace: isMetric || isShortLabel ? "nowrap" : "normal",
              boxShadow: isMetric ? "0 6px 18px rgba(0,0,0,0.45)" : undefined,
            }}
          >
            {isMetric ? (
              <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{body}</span>
            ) : (
              body
            )}
            {isMetric ? (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: "50%",
                  bottom: -5,
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderTop: "5px solid #2a2a2a",
                }}
              />
            ) : null}
          </div>,
          document.body,
        )}
    </>
  );
}
